import { Link } from "react-router-dom";

export default function Privacy() {
  return (
    <div style={styles.page}>
      <div style={styles.container}>
        <div style={styles.header}>
          <Link to="/register" style={{ textDecoration: "none" }}>
            <div style={styles.logoRow}>
              <div style={styles.logoMark}>M</div>
              <span style={styles.logoText}>MasterFileAI</span>
            </div>
          </Link>
        </div>

        <h1 style={styles.title}>Privacy Policy</h1>
        <p style={styles.updated}>Last updated: March 17, 2026</p>

        <div style={styles.content}>
          <Section title="Our Core Commitment">
            MasterFileAI does not sell, share, or use your data for any purpose other than delivering the Service to you. Your data is yours. We process it, clean it, and return it — nothing more.
          </Section>

          <Section title="1. Information We Collect">
            <strong>Account information:</strong> Name, work email, company name, and password (hashed — we never store plain-text passwords).<br /><br />
            <strong>Uploaded files:</strong> The vendor or customer data files you upload for processing. These are stored temporarily in Azure Blob Storage (Microsoft's enterprise cloud).<br /><br />
            <strong>Usage data:</strong> Job history, record counts, and processing status — used to show you your dashboard and billing history.<br /><br />
            <strong>Payment information:</strong> Payments are handled entirely by Stripe. We never see or store your card number.
          </Section>

          <Section title="2. How We Use Your Information">
            We use your information solely to:
            <ul style={{ paddingLeft: 20, marginTop: 8, lineHeight: 2 }}>
              <li>Create and manage your account</li>
              <li>Process your uploaded files through our AI cleaning pipeline</li>
              <li>Return cleaned output files to you</li>
              <li>Send transactional emails (email verification, login codes, receipts)</li>
              <li>Calculate and process payments for your jobs</li>
            </ul>
            We do not use your data to train AI models. We do not use your data for marketing. We do not share your data with third parties except as described in Section 4.
          </Section>

          <Section title="3. Data Storage and Security">
            All data is stored in Microsoft Azure infrastructure within the United States. We use:
            <ul style={{ paddingLeft: 20, marginTop: 8, lineHeight: 2 }}>
              <li>Azure SQL Database for account and job metadata (encrypted at rest)</li>
              <li>Azure Blob Storage for uploaded and output files (encrypted at rest)</li>
              <li>TLS/HTTPS for all data in transit</li>
              <li>bcrypt hashing for passwords</li>
              <li>Session tokens with 30-day expiry</li>
            </ul>
            Uploaded files are retained for 90 days after processing, then permanently deleted.
          </Section>

          <Section title="4. Third-Party Services">
            We use a small number of trusted third-party services to operate:
            <ul style={{ paddingLeft: 20, marginTop: 8, lineHeight: 2 }}>
              <li><strong>Microsoft Azure</strong> — cloud infrastructure, database, file storage, email delivery</li>
              <li><strong>Stripe</strong> — payment processing (subject to Stripe's Privacy Policy)</li>
              <li><strong>Anthropic Claude API</strong> — AI enrichment processing of your data records</li>
            </ul>
            Each of these services is bound by their own privacy policies and enterprise data processing agreements.
          </Section>

          <Section title="5. Data We Do Not Collect">
            We do not use cookies for tracking. We do not use analytics platforms (Google Analytics, Mixpanel, etc.). We do not collect device fingerprints, IP geolocation, or browsing history.
          </Section>

          <Section title="6. Your Rights">
            You have the right to:
            <ul style={{ paddingLeft: 20, marginTop: 8, lineHeight: 2 }}>
              <li>Access all data we hold about you</li>
              <li>Request deletion of your account and all associated data</li>
              <li>Export your job history and output files</li>
              <li>Correct inaccurate account information</li>
            </ul>
            To exercise any of these rights, email privacy@masterfileai.com. We will respond within 30 days.
          </Section>

          <Section title="7. Data Retention">
            Account data is retained for as long as your account is active. Uploaded input files and output files are deleted 90 days after processing. If you delete your account, all data is permanently deleted within 30 days.
          </Section>

          <Section title="8. Children's Privacy">
            The Service is intended for business use by adults. We do not knowingly collect data from anyone under 18 years of age.
          </Section>

          <Section title="9. Changes to This Policy">
            We may update this Privacy Policy. We will notify you of material changes via email. Continued use of the Service after notification constitutes acceptance.
          </Section>

          <Section title="10. Contact">
            For privacy questions or requests: privacy@masterfileai.com
          </Section>
        </div>

        <div style={styles.footer}>
          <Link to="/register" style={styles.backLink}>← Back to registration</Link>
          <Link to="/terms" style={styles.backLink}>Terms of Service →</Link>
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div style={{ marginBottom: 28 }}>
      <h2 style={{ fontSize: 16, fontWeight: 600, color: "#1f2937", marginBottom: 8 }}>{title}</h2>
      <div style={{ fontSize: 14, color: "#4b5563", lineHeight: 1.75 }}>{children}</div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: "100vh",
    background: "#f8f9fb",
    padding: "40px 16px",
    fontFamily: "'Inter', system-ui, sans-serif",
  },
  container: {
    maxWidth: 720,
    margin: "0 auto",
    background: "#ffffff",
    border: "1px solid #e2e6ef",
    borderRadius: 16,
    padding: "48px 56px",
    boxShadow: "0 4px 24px rgba(0,0,0,0.06)",
  },
  header: { marginBottom: 32 },
  logoRow: { display: "flex", alignItems: "center", gap: 10 },
  logoMark: {
    width: 36, height: 36, borderRadius: 10,
    background: "linear-gradient(135deg, #6c63ff, #3ecfcf)",
    display: "flex", alignItems: "center", justifyContent: "center",
    color: "#fff", fontWeight: 700, fontSize: 18,
  },
  logoText: { color: "#111827", fontWeight: 700, fontSize: 18, letterSpacing: "-0.3px" },
  title: { fontSize: 28, fontWeight: 700, color: "#111827", margin: "0 0 6px" },
  updated: { fontSize: 13, color: "#9ca3af", margin: "0 0 36px" },
  content: { borderTop: "1px solid #e2e6ef", paddingTop: 32 },
  footer: {
    borderTop: "1px solid #e2e6ef",
    paddingTop: 24,
    marginTop: 16,
    display: "flex",
    justifyContent: "space-between",
  },
  backLink: { color: "#6c63ff", fontSize: 14, textDecoration: "none", fontWeight: 500 },
};
